import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronRight } from 'lucide-react';
import { formatTimeAgo } from '../../utils/timeAgo';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { useCompanyId } from '../../hooks/useCompanyId';
import { buildDepartmentWhatsNewItems, WHATS_NEW_KIND_META } from '../../services/whatsNewFeed';

// Emoji + tile styling per feed item kind lives in the shared feed service so the
// dashboard and every department card render items identically.
const KIND_META = WHATS_NEW_KIND_META;

/**
 * Unified "What's New" feed card: portal updates, new courses/lessons, new
 * course documents, and new template document versions — one list.
 *
 * Pass `department` (e.g. "sales") to scope the feed to that department only:
 * courses/lessons whose course category matches, plus template versions from
 * that pillar — general updates and course documents are excluded.
 */
const DepartmentWhatsNewCard = ({ className = '', department = null, limit }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile } = useResponsive();
  const { companyId } = useCompanyId();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const dept = department ? String(department).toLowerCase() : null;
  const maxItems = limit ?? (dept ? 5 : 8);

  useEffect(() => {
    if (!currentUser) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const collected = await buildDepartmentWhatsNewItems({
        department: dept,
        currentUser,
        companyId,
        maxItems,
      });
      if (cancelled) return;
      setItems(collected);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, dept, maxItems, companyId]);

  const isFresh = (date) => {
    const t = new Date(date).getTime();
    return Number.isFinite(t) && Date.now() - t < 14 * 24 * 60 * 60 * 1000;
  };

  return (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col ${className}`}>
      <div className={`flex items-center justify-center gap-2 ${isMobile ? 'p-4 pb-3' : 'p-5 pb-4'}`}>
        <span className="text-base leading-none" aria-hidden="true">✨</span>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">What's New</h2>
      </div>
      <div className={`flex-1 ${isMobile ? 'px-2 pb-2' : 'px-3 pb-3'}`}>
        {loading ? (
          <div className="space-y-2 px-2 pb-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 animate-pulse">
                <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                  <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Sparkles className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nothing new here yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              New updates, courses, documents, and document versions will show up here.
            </p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {items.slice(0, maxItems).map((item) => {
              const meta = KIND_META[item.kind] || KIND_META.update;
              const onOpen = item.path
                ? () => navigate(item.path)
                : item.url
                ? () => window.open(item.url, '_blank', 'noopener,noreferrer')
                : null;
              const content = (
                <>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.tile}`}>
                    <span className="text-xl leading-none" aria-hidden="true">{item.emoji || meta.emoji}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{item.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {meta.label}
                      {item.subtitle ? ` · ${item.subtitle}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isFresh(item.date) && (
                      <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wide">
                        New
                      </span>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{formatTimeAgo(item.date)}</span>
                    {onOpen && <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />}
                  </div>
                </>
              );
              return onOpen ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={onOpen}
                  className="w-full text-left flex items-center gap-3 rounded-lg px-2.5 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  {content}
                </button>
              ) : (
                <div key={item.id} className="flex items-center gap-3 rounded-lg px-2.5 py-2.5">
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentWhatsNewCard;
